import { useState } from 'react';
import { Check, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { badgeVariants } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useGetActiveBitsQuery } from '@/redux/bit/bit.api.slice';
import { IBitPublic, IBitBrandRef } from '@/redux/bit/bit.d';
import { IBitType, IBitUsedUnit, ILaserOrEndmill } from '@/redux/post/post.d';
import { bitKey } from '../utils/fileHelpers';

/** Cutter diameter, the one spec a card leads with — it's how a bit is asked
 * for out loud ("the quarter-inch downcut"). */
function bitSize(bit: IBitPublic): string | null {
  if (bit.toolUsed == null) return null;
  return bit.toolUnit === IBitUsedUnit.INCHES
    ? `${bit.toolUsed}"`
    : `${bit.toolUsed} ${bit.toolUnit ?? ''}`.trim();
}

/** `format(value)` when the value is set, else null — one optional spec field. */
function optSpec(
  value: number | null | undefined,
  format: (v: number) => string
): string | null {
  return value != null ? format(value) : null;
}

/** The size/geometry fields a bit may carry, as one line. All four are optional
 * and independent. */
function bitSpecs(bit: IBitPublic): string[] {
  return [
    bit.bitType ?? null,
    bitSize(bit),
    optSpec(bit.vBitDegree, (d) => `${d}°`),
    optSpec(bit.tipRadius, (r) => `⌀ ${r} ${bit.toolUnit ?? ''}`.trim()),
  ].filter((spec): spec is string => Boolean(spec));
}

function BitThumb({ bit, size }: { bit: IBitPublic; size: string }) {
  const laser = bit.laserOrEndmill === ILaserOrEndmill.LASER;
  return bit.imageUrl ? (
    <img
      src={bit.imageUrl}
      alt=""
      className={cn(
        'rounded bg-white border shrink-0',
        laser ? 'object-contain' : 'object-cover',
        size
      )}
    />
  ) : (
    <div className={cn('rounded bg-muted shrink-0', size)} />
  );
}

/** One catalog entry per brand: a bit sold by N brands is N cards, since the
 * brand is part of what gets saved. A brand-less bit is one card. */
type BitItem = { bit: IBitPublic; brand?: IBitBrandRef };

function toItems(bits: IBitPublic[]): BitItem[] {
  return bits.flatMap((bit) =>
    bit.brands.length ? bit.brands.map((brand) => ({ bit, brand })) : [{ bit }]
  );
}

function matches(item: BitItem, query: string): boolean {
  const haystack = [item.bit.name, item.brand?.name, ...bitSpecs(item.bit)]
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

function TypeChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  // The shared badge, made clickable: an active filter is a `default` badge, an
  // idle one an `outline`. Same pill, same type scale, same tokens as every
  // other badge on the page.
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        badgeVariants({ variant: active ? 'default' : 'outline' }),
        'px-3 py-1 cursor-pointer',
        !active && 'text-muted-foreground hover:border-border-strong'
      )}
    >
      {label}
    </button>
  );
}

/** The card's title: the bit name, with size appended when it has one. */
function nameWithSize(name: string, size: string | null): string {
  return size ? `${name} · ${size}` : name;
}

/** Top-right state light. The whole card is the hit area, so this is an
 * indicator, not a second button to aim at. */
function PickBadge({ picked }: { picked: boolean }) {
  return (
    <span
      className={cn(
        'absolute top-2.5 right-2.5 w-5 h-5 rounded-full border flex items-center justify-center transition-colors',
        picked
          ? 'bg-primary border-primary text-primary-foreground'
          : 'text-muted-foreground group-hover:border-primary group-hover:text-primary'
      )}
    >
      {picked ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
    </span>
  );
}

/** Name (with size), brand, and the rest of the geometry — brand and meta shown
 * only when present. */
function BitInfo({
  bit,
  brand,
  size,
  meta,
}: {
  bit: IBitPublic;
  brand?: IBitBrandRef;
  size: string | null;
  meta: string[];
}) {
  return (
    <div className="min-w-0 flex-1">
      <div
        title={nameWithSize(bit.name, size)}
        className="text-[13px] font-semibold truncate"
      >
        {bit.name}
        {size && ` · ${size}`}
      </div>
      {brand && (
        <div
          title={brand.name}
          className="text-[11px] text-muted-foreground truncate mt-0.5"
        >
          {brand.name}
        </div>
      )}
      {meta.length > 0 && (
        <div
          title={meta.join(' · ')}
          className="text-[10px] font-tertiary text-primary/75 truncate mt-1"
        >
          {meta.join(' · ')}
        </div>
      )}
    </div>
  );
}

function BitCard({
  item,
  picked,
  onPick,
}: {
  item: BitItem;
  picked: boolean;
  onPick: () => void;
}) {
  const { bit, brand } = item;
  const size = bitSize(bit);
  // Size is in the title, so the line below it is the rest of the geometry.
  const meta = bitSpecs(bit).filter((spec) => spec !== size);

  return (
    <button
      type="button"
      onClick={onPick}
      aria-label={nameWithSize(bit.name, size)}
      className={cn(
        'group relative flex items-center gap-3 rounded-lg border bg-card text-card-foreground shadow-sm p-3 pr-9 text-left transition-[color,background-color,border-color,box-shadow] duration-fast',
        picked
          ? 'ring-2 ring-primary'
          : 'hover:border-border-strong hover:shadow-md'
      )}
    >
      <BitThumb bit={bit} size="w-28 h-14" />
      <BitInfo bit={bit} brand={brand} size={size} meta={meta} />
      <PickBadge picked={picked} />
    </button>
  );
}

/** The type filter chips — "All" plus one per bit type present. Hidden when the
 * catalog has no types to filter by. */
function TypeFilterRow({
  types,
  active,
  onSelect,
}: {
  types: IBitType[];
  active: IBitType | undefined;
  onSelect: (type: IBitType | undefined) => void;
}) {
  if (types.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-3">
      <TypeChip
        label="All"
        active={!active}
        onClick={() => onSelect(undefined)}
      />
      {types.map((type) => (
        <TypeChip
          key={type}
          label={type}
          active={active === type}
          onClick={() => onSelect(type)}
        />
      ))}
    </div>
  );
}

/** The scrollable grid of pickable cards, with an empty state once a search
 * comes up dry (but not while the catalog is still loading). */
function BitGrid({
  shown,
  picked,
  isLoading,
  onPick,
}: {
  shown: BitItem[];
  picked: string[];
  isLoading: boolean;
  onPick: (bit: IBitPublic, brand?: IBitBrandRef) => void;
}) {
  return (
    // p-1, not pr-1: a picked card's ring is drawn outside its box and the
    // scroll container would clip it flush at the edges.
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 mt-3 max-h-[380px] overflow-y-auto p-1">
      {shown.map((item) => {
        const key = bitKey(item.bit._id, item.brand?._id ?? '');
        return (
          <BitCard
            key={key}
            item={item}
            picked={picked.includes(key) || picked.includes(item.bit._id)}
            onPick={() => onPick(item.bit, item.brand)}
          />
        );
      })}
      {shown.length === 0 && !isLoading && (
        <div className="col-span-full py-8 text-center text-xs text-muted-foreground">
          No bits match your search.
        </div>
      )}
    </div>
  );
}

/** The bit catalog: search, filter by type, pick. Rendered as the body of the
 * row's picker dialog. */
export default function BitCatalog({
  picked,
  suggestedType,
  onPick,
}: {
  /** `bitId:brandId` (or bare bit id) of what is already picked, ticked in the
   * grid. */
  picked: string[];
  /** The type the file's G-code calls, pre-selected on open. */
  suggestedType?: string;
  /** Click: add to (or take out of) the selection. */
  onPick: (bit: IBitPublic, brand?: IBitBrandRef) => void;
}) {
  const { data: bits = [], isLoading } = useGetActiveBitsQuery();
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<IBitType | undefined>(
    suggestedType as IBitType | undefined
  );

  const catalog = bits;
  const types = Object.values(IBitType).filter((type) =>
    catalog.some((bit) => bit.bitType === type)
  );

  // A filter with no chip is a dead end — the grid empties and there is nothing
  // to click to get back. The parser's guess reaches here unvalidated, and it
  // has types of its own ("Endmill") that the catalog has never heard of.
  const active =
    typeFilter && types.includes(typeFilter) ? typeFilter : undefined;

  const q = query.trim().toLowerCase();
  const shown = toItems(catalog)
    .filter((item) => !active || item.bit.bitType === active)
    .filter((item) => !q || matches(item, q));

  return (
    <div className="rounded-xl">
      <Input
        autoFocus
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          // A search spans the catalog; the seeded chip would silently exclude it.
          if (e.target.value.trim()) setTypeFilter(undefined);
        }}
        placeholder={
          isLoading
            ? 'Loading catalog…'
            : `Search ${catalog.length} bits and lasers — brand, type, size`
        }
        className="h-11 bg-background text-sm"
      />

      <TypeFilterRow types={types} active={active} onSelect={setTypeFilter} />

      <BitGrid
        shown={shown}
        picked={picked}
        isLoading={isLoading}
        onPick={onPick}
      />
    </div>
  );
}
